import { createContext, useContext } from 'react';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';

export interface TodoListContextValue {
  // Data
  isOwner: boolean;
  currentUserId: string | undefined;
  visibleSectionIds: Set<string> | undefined;

  // Callbacks (optional — not provided for public/read-only views)
  onToggleItem?: (item: TodoItemResponse) => void;
  onEditItem?: (item: TodoItemResponse) => void;
  onDeleteItem?: (itemId: string) => void;
  onEditSection?: (section: TodoSectionResponse) => void;
  onDeleteSection?: (sectionId: string) => void;
  onCreateItem?: (input: { title: string; description?: string; sectionId?: string }) => void;
  onCreateSection?: (input: {
    name: string;
    description?: string;
    parentSectionId?: string;
    sectionType?: 'timeframe' | 'category';
  }) => void;
  onReorderItems?: (sectionId: string | null, itemIds: string[]) => void;
  onReorderSections?: (sectionIds: string[]) => void;
  onUpdateItemTitle?: (itemId: string, title: string) => void;
  onCreateItemBelow?: (
    sectionId: string | null,
    afterItemId: string,
    title: string,
  ) => Promise<void>;
  onToggleHiddenFromPublic?: (sectionId: string, hidden: boolean) => void;
}

const TodoListContext = createContext<TodoListContextValue | null>(null);

export function TodoListProvider({
  value,
  children,
}: {
  value: TodoListContextValue;
  children: React.ReactNode;
}) {
  return <TodoListContext.Provider value={value}>{children}</TodoListContext.Provider>;
}

export function useTodoListContext(): TodoListContextValue {
  const ctx = useContext(TodoListContext);
  if (!ctx) {
    throw new Error('useTodoListContext must be used within a TodoListProvider');
  }
  return ctx;
}
